import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { api } from "@/lib/apiClient";
import { displayDoctorName, formatIST } from "@/lib/utils";
import { IndiaMosaicMap } from "@/components/IndiaMosaicMap";
import { CountUp } from "@/components/CountUp";
import { Button } from "@/components/ui/button";
import { ORG, CAMPAIGN, PLEDGE_TARGET } from "@/lib/constants";
import { ArrowLeft, RotateCcw, Target, Loader2 } from "lucide-react";

const TRICOLOR = ["#FF9933", "#FFFFFF", "#138808", "#06038D"];

function Avatar({ pledge, size = "w-14 h-14", text = "text-base" }) {
  const initials = (pledge.name || "").replace(/^Dr\.?\s*/i, "").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return pledge.photo ? (
    <img src={pledge.photo} alt={pledge.name} className={`${size} rounded-full object-cover shrink-0 ring-2 ring-[#FF9933]/40`} />
  ) : (
    <span className={`grid place-items-center ${size} rounded-full bg-gradient-to-br from-[#0056D2] to-[#138808] text-white ${text} font-display font-bold shrink-0`}>
      {initials || "DR"}
    </span>
  );
}

// Replay of the exact moment a doctor's entry landed on the Live Pledge Wall:
// same fly-in + spotlight animation, with the wall counter frozen at the
// pledge count as it stood when they submitted.
export default function MomentReplayPage() {
  const { certId } = useParams();
  const [moment, setMoment] = useState(undefined); // undefined=loading, false=not found
  const [mapPledges, setMapPledges] = useState([]);
  const [incoming, setIncoming] = useState(false);
  const [landed, setLanded] = useState(false);

  useEffect(() => {
    api.get(`/pledges/moment/${encodeURIComponent(certId)}`)
      .then((r) => setMoment(r.data))
      .catch(() => setMoment(false));
    api.get("/pledges/recent?limit=200&with_photo=true").then((r) => setMapPledges(r.data)).catch(() => {});
  }, [certId]);

  const replay = useCallback(() => {
    setLanded(false);
    setIncoming(true);
    setTimeout(() => {
      setIncoming(false);
      setLanded(true);
      confetti({ particleCount: 120, spread: 100, startVelocity: 50, colors: TRICOLOR, origin: { x: 0.5, y: 0.6 }, zIndex: 60 });
    }, 1600);
  }, []);

  // Auto-play once the data is in.
  useEffect(() => {
    if (moment && typeof moment === "object") {
      const t = setTimeout(replay, 800);
      return () => clearTimeout(t);
    }
  }, [moment, replay]);

  if (moment === undefined) {
    return <div className="min-h-screen bg-[#faf6ee] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }
  if (moment === false) {
    return (
      <div className="min-h-screen bg-[#faf6ee] grid place-items-center px-6 text-center">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-slate-900">Certificate not found</h1>
          <p className="mt-2 text-slate-500">Please check the Certificate ID and try again.</p>
          <Link to="/my-moment" className="inline-block mt-5"><Button className="rounded-full bg-[#0056D2] text-white">Try again</Button></Link>
        </div>
      </div>
    );
  }

  const p = moment.pledge;
  const ordinal = moment.ordinal;
  const name = displayDoctorName(p.name);

  return (
    <div data-testid="moment-replay" className="min-h-screen bg-[#faf6ee] flex flex-col">
      <div className="tricolor-bar h-1 w-full shrink-0" aria-hidden="true" />

      {/* Wall-style header frozen at their moment */}
      <header className="shrink-0 px-4 lg:px-8 py-2.5 flex flex-wrap items-center justify-between gap-x-5 gap-y-2 bg-[#1F2B7B] text-white shadow-md">
        <div className="flex items-center gap-3 min-w-0">
          <span className="grid place-items-center w-12 h-12 rounded-xl bg-white p-1 shrink-0">
            <img src={ORG.logo} alt={`${ORG.name} logo`} className="w-full h-full" />
          </span>
          <div className="min-w-0">
            <h1 className="font-display font-extrabold text-lg lg:text-2xl leading-none uppercase tracking-wide">{CAMPAIGN.title}</h1>
            <p className="text-[10px] lg:text-[11px] text-blue-100 tracking-wide mt-1">Your moment on the wall · {formatIST(p.created_at)}</p>
          </div>
        </div>
        <div data-testid="moment-counter" className="flex items-center gap-2 font-bold">
          <Target className="w-4 h-4 text-[#FF9933]" />
          <span className="font-display text-2xl"><CountUp value={ordinal} /></span>
          <span className="text-blue-200 font-medium text-sm">/ {PLEDGE_TARGET.toLocaleString("en-IN")} pledges at your moment</span>
        </div>
      </header>

      <div className="flex-1 max-w-3xl w-full mx-auto px-4 py-4 flex flex-col">
        <div className="flex items-center justify-between gap-3 mb-2">
          <Link to="/my-moment" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0056D2]" data-testid="replay-back">
            <ArrowLeft className="w-4 h-4" /> Check another certificate
          </Link>
          <Button data-testid="replay-again-btn" onClick={replay} size="sm" variant="outline" className="rounded-full border-slate-300">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Replay
          </Button>
        </div>

        <div className="relative flex-1 min-h-[420px]">
          <IndiaMosaicMap pledges={mapPledges} />

          {/* their entry flying in — same animation as live arrivals */}
          <AnimatePresence>
            {incoming && (
              <motion.div
                key={`in-${Date.now()}`}
                data-testid="moment-incoming"
                initial={{ opacity: 0, y: 140, scale: 1.05 }}
                animate={{ opacity: [0, 1, 1, 0.9, 0], y: [140, 0, 0, -40, -110], scale: [1.05, 1, 1, 0.55, 0.12] }}
                transition={{ duration: 1.6, times: [0, 0.25, 0.6, 0.85, 1], ease: "easeInOut" }}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none"
              >
                <div className="flex items-center gap-3 rounded-2xl bg-white border-2 border-[#FF9933] shadow-2xl px-5 py-4 w-72">
                  <Avatar pledge={p} />
                  <div className="min-w-0">
                    <p className="font-display font-extrabold text-slate-900 truncate">{name}</p>
                    <p className="text-xs text-[#0056D2] font-semibold truncate">{p.specialty}</p>
                    <p className="text-xs text-slate-500 truncate">{[p.city, p.state].filter(Boolean).join(", ")}</p>
                  </div>
                </div>
                <p className="mt-2 text-center text-xs font-bold text-[#138808]">joined the wall! 🎉</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* persistent spotlight, like the 35s live spotlight */}
          <AnimatePresence>
            {landed && (
              <motion.div
                key="spot"
                data-testid="moment-spotlight"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                className="absolute top-2 right-0 z-10"
              >
                <div className="flex items-center gap-3 rounded-2xl bg-white/95 backdrop-blur border-2 border-[#138808] shadow-xl px-4 py-3 w-64">
                  <Avatar pledge={p} size="w-12 h-12" text="text-sm" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-[#138808]">Just joined 🎉</p>
                    <p className="font-display font-bold text-sm text-slate-900 truncate">{name}</p>
                    <p className="text-xs text-slate-500 truncate">{[p.specialty, p.city].filter(Boolean).join(" | ")}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-3 rounded-2xl bg-slate-950 text-white px-6 py-4 text-center shrink-0">
          <p className="text-sm lg:text-base">
            <span className="font-display font-extrabold text-[#FF9933]">{name}</span> was pledge
            <span className="font-display font-extrabold text-[#7CFC9B]"> #{ordinal.toLocaleString("en-IN")}</span> on the Healthy India Pledge Wall
          </p>
          <p className="mt-1 text-xs text-slate-400">{formatIST(p.created_at)} · Certificate {certId.toUpperCase()} · An initiative by {ORG.name}</p>
        </div>
      </div>
    </div>
  );
}
